import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, Download, Trash, FileText } from "@phosphor-icons/react";

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const DocumentsPage = () => {
  const [items, setItems] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [engagementId, setEngagementId] = useState("");
  const [note, setNote] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    const [d, e] = await Promise.all([api.get("/documents"), api.get("/engagements")]);
    setItems(d.data.items);
    setEngagements(e.data.items);
  };

  useEffect(() => {
    load();
  }, []);

  const onUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (engagementId) fd.append("engagement_id", engagementId);
    if (note) fd.append("note", note);
    try {
      await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      setNote("");
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const download = (id) => {
    const token = localStorage.getItem("ledgerline_token");
    // Use fetch to attach auth header, then save blob
    fetch(`${API}/documents/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("download failed");
        const blob = await r.blob();
        const disp = r.headers.get("content-disposition") || "";
        const m = /filename="?([^"]+)"?/.exec(disp);
        const name = m ? m[1] : "document";
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
        load();
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div data-testid="documents-page" className="space-y-8">
      <div className="pb-8 border-b border-[#1A2F24]/12">
        <p className="label-eyebrow">Portal</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#112018] mt-2">Documents</h1>
        <p className="text-[#606C64] mt-2 text-sm max-w-xl">
          Securely store, share and track every file across engagements. History is
          recorded for each upload, download and delete.
        </p>
      </div>

      {/* Upload */}
      <div className="card-flat">
        <p className="label-eyebrow">Upload</p>
        <h2 className="font-serif text-2xl text-[#112018] mt-1">New document</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div>
            <label className="label-eyebrow block mb-2">Associate with engagement</label>
            <select
              data-testid="upload-engagement-select"
              className="input-clean"
              value={engagementId}
              onChange={(e) => setEngagementId(e.target.value)}
            >
              <option value="">— Unassociated —</option>
              {engagements.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.client_name} · {e.service_type}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label-eyebrow block mb-2">Note</label>
            <input
              data-testid="upload-note"
              className="input-clean"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="W-2 for FY2025…"
            />
          </div>
        </div>
        <div className="mt-4 border border-dashed border-[#1A2F24]/30 p-8 text-center">
          <input
            ref={inputRef}
            data-testid="file-input"
            type="file"
            onChange={(e) => onUpload(e.target.files?.[0])}
            className="hidden"
            id="upload-input"
          />
          <label
            htmlFor="upload-input"
            className="cursor-pointer inline-flex items-center gap-2 btn-primary"
            data-testid="upload-trigger"
          >
            <UploadSimple size={14} /> Choose file
          </label>
          <p className="label-eyebrow mt-3">Any file type. Max 50 MB.</p>
        </div>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="card-flat text-center py-12">
          <FileText size={32} className="mx-auto text-[#A8977B]" />
          <p className="font-serif text-2xl text-[#112018] mt-3">No documents yet.</p>
          <p className="text-sm text-[#606C64] mt-1">
            Upload your first file to start tracking history.
          </p>
        </div>
      ) : (
        <div className="card-flat p-0 overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F7F6F2] border-b border-[#1A2F24]/12">
              <tr className="text-left">
                <th className="label-eyebrow p-4">Document</th>
                <th className="label-eyebrow p-4 hidden md:table-cell">Size</th>
                <th className="label-eyebrow p-4 hidden md:table-cell">Uploaded</th>
                <th className="label-eyebrow p-4 hidden lg:table-cell">History</th>
                <th className="label-eyebrow p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr
                  key={d.id}
                  className="border-b border-[#1A2F24]/8 hover:bg-[#F7F6F2] transition-colors"
                  data-testid={`doc-row-${d.id}`}
                >
                  <td className="p-4">
                    <p className="text-[#112018]">{d.original_name}</p>
                    {d.note && (
                      <p className="text-xs text-[#606C64] mt-1 italic">{d.note}</p>
                    )}
                  </td>
                  <td className="p-4 hidden md:table-cell font-mono text-xs">
                    {formatBytes(d.size_bytes)}
                  </td>
                  <td className="p-4 hidden md:table-cell font-mono text-xs">
                    {new Date(d.uploaded_at).toLocaleString()}
                  </td>
                  <td className="p-4 hidden lg:table-cell">
                    <span className="font-mono text-xs">
                      {(d.history || []).length} events
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => download(d.id)}
                        className="btn-outline px-3 py-1.5"
                        data-testid={`doc-download-${d.id}`}
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => remove(d.id)}
                        className="btn-outline px-3 py-1.5 hover:!border-[#C94A3D] hover:!text-[#C94A3D]"
                        data-testid={`doc-delete-${d.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
